import axios, { AxiosError } from 'axios';
import { TIITSign } from './iit/types';
import { errorMessages } from '~/constants/errorMessages.ts';
import { ERROR_CODES, HTTP_ERROR_STATUS } from '~/constants/errorCodes.ts';
import { TThunkError } from '~/store/covenants/types.ts';

type TBackendValidationError = {
  message?: unknown;
};

const getBackendErrorMessage = (data: unknown): string | undefined => {
  if (Array.isArray(data)) {
    return data
      .map((item: TBackendValidationError) =>
        typeof item?.message === 'string' ? item.message.trim() : ''
      )
      .find(Boolean);
  }

  if (data && typeof data === 'object') {
    const message = (data as { message?: unknown }).message;

    return typeof message === 'string' && message.trim() ? message : undefined;
  }

  return undefined;
};

export const thunkErrorHandler = <TRejectValue = unknown, TRejectedResult = unknown>(
  error: unknown,
  rejectWithValue: (value: TRejectValue) => TRejectedResult
): TRejectedResult => {
  const errorEntity = error as { data: TThunkError };
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message: string; errorCode?: string }>;
    const responseData = axiosError.response?.data;
    const responseStatus = axiosError.response?.status ?? error?.status;

    if (responseData?.errorCode === ERROR_CODES.INTERNAL_SERVER_ERROR.toString()) {
      return rejectWithValue({
        message: errorMessages.SERVICE_UNAVAILABLE,
        status: 500,
      } as TRejectValue);
    }

    return rejectWithValue({
      data: responseData,
      errorCode: responseData?.errorCode,
      message: getBackendErrorMessage(responseData) ?? axiosError.message,
      status: responseStatus,
    } as TRejectValue);
  } else if (errorEntity.data?.message) {
    if (errorEntity?.data?.errorCode === ERROR_CODES.INTERNAL_SERVER_ERROR.toString()) {
      return rejectWithValue({
        ...errorEntity?.data,
        message: errorMessages.SERVICE_UNAVAILABLE,
      } as TRejectValue);
    }
    return rejectWithValue({
      ...errorEntity.data,
    } as TRejectValue);
  } else {
    return rejectWithValue({ message: errorMessages.SERVICE_UNAVAILABLE } as TRejectValue);
  }
};

export function thunkErrorCreatedIdentification<TRejectedResult = unknown>(
  error: unknown,
  rejectWithValue: (value: TThunkError) => TRejectedResult
) {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message: string; errorCode?: string }>;
    const errorCodes = [
      ERROR_CODES.DATA_NOT_FOUND_EDR,
      ERROR_CODES.MANAGER_NOT_FOUND,
      ERROR_CODES.STATUS_ERROR,
    ];
    if (
      axiosError?.response?.status === HTTP_ERROR_STATUS.INTERNAL_SERVER_ERROR &&
      errorCodes.includes(Number(axiosError.response.data.errorCode))
    ) {
      return rejectWithValue({
        message: axiosError.response.data.message,
        status: 500,
      });
    }
  }
  return thunkErrorHandler(error, rejectWithValue);
}

export const getFormDataFiles = (files: TIITSign[]) => {
  const formData = new FormData();

  files.forEach((fileObj) => {
    const mimeType = 'application/pkcs7-signature';

    const byteString = atob(fileObj.base64File);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    formData.append(
      'files',
      new File([new File([ab], mimeType)], fileObj.name, { type: mimeType })
    );
  });
  return formData;
};

export const maskPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  const digitsOnly = phoneNumber.replace(/\D/g, '');

  if (digitsOnly.length >= 10) {
    const countryCode = digitsOnly.slice(0, 3);
    const operatorCode = digitsOnly.slice(3, 5);
    const firstTwo = digitsOnly.slice(5, 7);
    const lastTwo = digitsOnly.slice(-2);

    return `+${countryCode}${operatorCode}${firstTwo}***${lastTwo}`;
  }

  return phoneNumber;
};

export const getAttemptsWord = (remaining: number): string => {
  if (remaining === 1) return 'спроба';
  if (remaining >= 2 && remaining <= 4) return 'спроби';
  return 'спроб';
};

export const extractToken = (
  authorizationHeader?: string,
  bodyToken?: string,
  fallback?: string | null
) => {
  const rawToken = authorizationHeader || bodyToken || fallback || null;

  if (!rawToken) {
    return null;
  }

  const normalizedToken = rawToken.trim();

  if (normalizedToken.toLowerCase().startsWith('bearer ')) {
    return normalizedToken.slice(7).trim() || null;
  }

  return normalizedToken;
};
